import React from "react";
import { Link } from "react-router-dom";
import SeoHead from "../../components/seo/SeoHead";
import SeoMarketingShell from "../../components/seo/SeoMarketingShell";
import { SEO_BASE_URL, cityPages } from "../../data/seoPages";

const GLASS_CARD =
  "rounded-3xl border border-[#E5E7EB] bg-white/95 backdrop-blur-2xl shadow-[0_24px_80px_rgba(15,23,42,0.12)]";
const GLASS_CARD_SOFT =
  "rounded-3xl border border-[#E5E7EB] bg-white/92 backdrop-blur-2xl shadow-[0_18px_60px_rgba(15,23,42,0.10)]";
const PRIMARY_CTA =
  "rounded-full px-7 py-3 text-white text-[12px] font-semibold tracking-[0.12em] uppercase bg-gradient-to-r from-[#FF4D4D] via-[#FF6B6B] to-[#FF4D4D] shadow-[0_14px_40px_rgba(248,113,113,0.45)] hover:shadow-[0_18px_54px_rgba(248,113,113,0.55)] transition-all";
const SECONDARY_CTA =
  "rounded-full px-7 py-3 text-[#111827] text-[12px] font-semibold tracking-[0.08em] uppercase border border-[#E5E7EB] bg-white hover:bg-[#F9FAFB] transition-all";

function MarketCard({ cityItem }) {
  return (
    <Link
      to={`/locations/${cityItem.city}/airbnb-grocery-delivery`}
      className="group flex h-full flex-col rounded-[30px] border border-[#E5E7EB] bg-white/92 p-6 shadow-[0_14px_36px_rgba(15,23,42,0.05)] transition-all hover:-translate-y-[2px] hover:shadow-[0_20px_50px_rgba(15,23,42,0.08)]"
    >
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-[12px] font-semibold tracking-[0.14em] text-[#EF4949]">City Guide</p>
          <h2 className="mt-2 font-segoe text-[24px] font-semibold text-[#111827]">
            {cityItem.displayName}
          </h2>
        </div>
        <span className="rounded-full border border-[#FECACA] bg-[#FFF5F5] px-3 py-1 text-[11px] font-semibold tracking-[0.08em] text-[#991B1B]">
          {cityItem.indexable ? "Live" : "Planned"}
        </span>
      </div>

      <p className="mt-4 flex-grow text-[15px] leading-7 text-[#6B7280]">{cityItem.hubSummary}</p>

      <div className="mt-5 flex flex-wrap gap-2">
        {(cityItem.cityTags || []).slice(0, 3).map((tag) => (
          <span
            key={tag}
            className="rounded-full border border-[#E5E7EB] bg-[#F9FAFB] px-3 py-1 text-[11px] font-semibold tracking-[0.06em] text-[#4B5563]"
          >
            {tag}
          </span>
        ))}
      </div>

      <p className="mt-5 text-[14px] font-semibold text-[#111827] transition-colors group-hover:text-[#EF4949]">
        View {cityItem.displayName} Page →
      </p>
    </Link>
  );
}

function LocationsHubPage() {
  const indexedCityPages = cityPages.filter((cityItem) => cityItem.indexable);
  const title = "Bellhopt Locations | Airbnb Grocery Delivery And City Guides";
  const description =
    "Explore Bellhopt city pages for Airbnb grocery delivery, pre-arrival grocery stocking, and concierge services. Each market page explains how Bellhopt supports local short-term rental operations.";

  const structuredData = [
    {
      "@context": "https://schema.org",
      "@type": "WebPage",
      name: title,
      url: `${SEO_BASE_URL}/locations/`,
      description,
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: "Locations",
          item: `${SEO_BASE_URL}/locations/`,
        },
      ],
    },
    {
      "@context": "https://schema.org",
      "@type": "ItemList",
      itemListElement: indexedCityPages.map((cityItem, index) => ({
        "@type": "ListItem",
        position: index + 1,
        name: cityItem.displayName,
        url: `${SEO_BASE_URL}/locations/${cityItem.city}/airbnb-grocery-delivery/`,
      })),
    },
  ];

  return (
    <SeoMarketingShell>
      <SeoHead
        title={title}
        description={description}
        canonical="/locations/"
        robots="index,follow"
        ogTitle="Bellhopt Locations"
        ogDescription={description}
        structuredData={structuredData}
      />

      <main className="w-[90%] max-w-[1100px] m-auto pt-24 pb-10 mobile:w-[95%]">
        <section className={`${GLASS_CARD} relative overflow-hidden p-8 mobile:p-5`}>
          <div className="pointer-events-none absolute inset-0">
            <div className="absolute -right-24 top-0 h-64 w-64 rounded-full bg-[#FFE4E6] blur-3xl opacity-70" />
            <div className="absolute bottom-0 left-0 h-56 w-56 rounded-full bg-[#FEE2E2] blur-3xl opacity-60" />
          </div>

          <div className="relative grid gap-6 lg:grid-cols-[minmax(0,1.15fr)_minmax(320px,0.85fr)] lg:items-end">
            <div>
              <p className="text-[13px] font-semibold tracking-[0.14em] text-[#EF4949]">
                Bellhopt Market Coverage
              </p>
              <h1 className="mt-3 font-segoe font-semibold text-[40px] mobile:text-[30px] text-[#111827] leading-tight">
                Bellhopt Locations For Airbnb Grocery Delivery And Concierge Services
              </h1>
              <p className="mt-4 text-[20px] mobile:text-[17px] text-[#6B7280] leading-8">
                This locations hub is the starting point for Bellhopt's market-specific pages. Each
                city page explains how pre-arrival grocery delivery, in-home stocking, and
                concierge execution fit the local operating reality for short-term rentals.
              </p>
              <div className="mt-7 flex flex-wrap gap-3">
                <a href="#markets" className={PRIMARY_CTA}>
                  Browse City Pages
                </a>
                <Link to="/for/property-managers" className={SECONDARY_CTA}>
                  For Property Managers
                </Link>
              </div>
            </div>

            <div className="rounded-[32px] border border-white/70 bg-white/85 p-6 shadow-[0_20px_50px_rgba(15,23,42,0.08)] backdrop-blur-xl">
              <div className="rounded-[26px] border border-[#FECACA] bg-[linear-gradient(180deg,rgba(255,245,245,0.95),rgba(255,255,255,0.92))] p-5">
                <p className="text-[12px] font-semibold tracking-[0.14em] text-[#EF4949]">
                  Why This Hub Exists
                </p>
                <h2 className="mt-3 font-segoe text-[26px] font-semibold leading-tight text-[#111827]">
                  A Real Market Index, Not A Thin Location Directory
                </h2>
                <p className="mt-3 text-[15px] leading-7 text-[#6B7280]">
                  Bellhopt's locations pages are designed to explain what matters in each market:
                  guest demand, host operations, access complexity, and where stocked arrivals create
                  the most value.
                </p>
              </div>

              <div className="mt-5 grid gap-3">
                {[
                  "Each city page includes local operating context, not just a city name swap.",
                  "Internal linking connects market pages to Bellhopt's broader knowledge base.",
                  "The hub helps users and search engines discover the full coverage set clearly.",
                ].map((item, index) => (
                  <div
                    key={item}
                    className="flex items-start gap-4 rounded-2xl border border-[#E5E7EB] bg-white/92 px-4 py-4"
                  >
                    <span className="mt-1 inline-flex h-8 w-8 items-center justify-center rounded-full bg-[#111827] text-[11px] font-semibold tracking-[0.08em] text-white">
                      0{index + 1}
                    </span>
                    <p className="text-[14px] leading-6 text-[#4B5563]">{item}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className={`mt-6 ${GLASS_CARD_SOFT} p-7 mobile:p-5`}>
          <h2 className="font-segoe font-semibold text-[30px] mobile:text-[24px] text-[#111827]">
            What Bellhopt City Pages Cover
          </h2>
          <p className="mt-3 max-w-[780px] text-[18px] mobile:text-[16px] text-[#6B7280] leading-8">
            Each market page is built to answer the core informational questions behind
            location-based search intent: how the service works in that city, what kinds of stays it
            fits best, what operators need for reliable execution, and why Bellhopt is more than a
            generic delivery option.
          </p>
          <div className="mt-6 grid gap-4 md:grid-cols-3">
            {[
              {
                title: "Local Guest Demand",
                desc: "Why guests in that market value a stocked home before check-in.",
              },
              {
                title: "Operator Reality",
                desc: "What hosts and property managers need to manage access, timing, and quality.",
              },
              {
                title: "Bellhopt Execution",
                desc: "How in-house concierge delivery, stocking, and organization support a stronger arrival standard.",
              },
            ].map((item, index) => (
              <div
                key={item.title}
                className={`rounded-[30px] border p-5 shadow-[0_10px_30px_rgba(15,23,42,0.05)] ${
                  index === 1
                    ? "border-[#DBEAFE] bg-[linear-gradient(180deg,rgba(248,250,252,0.98),rgba(239,246,255,0.96))]"
                    : "border-[#E5E7EB] bg-white/92"
                }`}
              >
                <p className={`text-[12px] font-semibold tracking-[0.14em] ${index === 1 ? "text-[#1D4ED8]" : "text-[#EF4949]"}`}>
                  Focus Area
                </p>
                <h3 className="mt-3 font-segoe text-[22px] font-semibold text-[#111827]">
                  {item.title}
                </h3>
                <p className="mt-3 text-[15px] leading-7 text-[#6B7280]">{item.desc}</p>
              </div>
            ))}
          </div>
        </section>

        <section id="markets" className={`mt-6 ${GLASS_CARD_SOFT} p-7 mobile:p-5`}>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <h2 className="font-segoe font-semibold text-[30px] mobile:text-[24px] text-[#111827]">
                Available City Pages
              </h2>
              <p className="mt-3 max-w-[780px] text-[18px] mobile:text-[16px] text-[#6B7280] leading-8">
                Start with the market that matches your portfolio or guest demand. Each city page is
                written as a standalone Bellhopt knowledge-base entry for that market.
              </p>
            </div>
            <div className="rounded-2xl border border-[#E5E7EB] bg-white/90 px-4 py-3 text-[13px] font-semibold tracking-[0.08em] text-[#374151]">
              {indexedCityPages.length} Indexed Markets
            </div>
          </div>

          <div className="mt-6 grid gap-4 sm:grid-cols-2">
            {indexedCityPages.map((cityItem) => (
              <MarketCard key={cityItem.city} cityItem={cityItem} />
            ))}
          </div>
        </section>

        <section className={`mt-6 ${GLASS_CARD_SOFT} p-7 mobile:p-5`}>
          <h2 className="font-segoe font-semibold text-[30px] mobile:text-[24px] text-[#111827]">
            Explore Bellhopt Beyond City Pages
          </h2>
          <p className="mt-3 max-w-[780px] text-[18px] mobile:text-[16px] text-[#6B7280] leading-8">
            The location pages connect to Bellhopt's broader informational content so guests, hosts,
            and property managers can understand the full service model, not just the city list.
          </p>
          <div className="mt-5 flex flex-wrap gap-3">
            <Link to="/how-it-works" className={SECONDARY_CTA}>
              How It Works
            </Link>
            <Link to="/pre-arrival-groceries" className={SECONDARY_CTA}>
              Pre-Arrival Groceries
            </Link>
            <Link to="/vacation-rental-grocery-delivery" className={SECONDARY_CTA}>
              Vacation Rental Grocery Delivery
            </Link>
            <Link to="/integrations" className={SECONDARY_CTA}>
              Integrations
            </Link>
          </div>
        </section>
      </main>
    </SeoMarketingShell>
  );
}

export default LocationsHubPage;
